/**
 * AiAssistantPanel — Floating AI assistant panel with header, message list, and input
 */
import { useState, useCallback, useRef } from 'react'
import { X, Maximize2, Minimize2, Send } from 'lucide-react'
import { AgentMessageList } from '../molecules/AgentMessageList'
import { AgentModeSwitcher } from '../molecules/AgentModeSwitcher'
import { AgentGuiRenderer } from './AgentGuiRenderer'
import { useAgentSession } from '@/hooks/useAgentSession'
import { useAgentSSE, type SSEEvent } from '@/hooks/useAgentSSE'
import type { AgentMessage, InteractionMode } from '@/lib/api/agent'

interface AiAssistantPanelProps {
  isOpen: boolean
  onClose: () => void
  userRole: string
  companyId: string
  permissions: string[]
}

export function AiAssistantPanel({ isOpen, onClose, userRole, companyId, permissions }: AiAssistantPanelProps) {
  const [expanded, setExpanded] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [streamingText, setStreamingText] = useState('')
  const [toolStatus, setToolStatus] = useState<{ name: string; status: 'started' | 'completed' | 'failed' }[]>([])
  const [guiElement, setGuiElement] = useState<any>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const streamingRef = useRef('')

  const session = useAgentSession(userRole, companyId)
  const sse = useAgentSSE()

  const handleEvent = useCallback((event: SSEEvent) => {
    switch (event.type) {
      case 'text':
        streamingRef.current += (event.data.token || '')
        setStreamingText(streamingRef.current)
        break
      case 'tool_started':
        setToolStatus(prev => [...prev, { name: event.data.tool_name, status: 'started' }])
        break
      case 'tool_completed':
        setToolStatus(prev => prev.map(t =>
          t.name === event.data.tool_name && t.status === 'started' ? { ...t, status: 'completed' } : t
        ))
        break
      case 'tool_failed':
        setToolStatus(prev => prev.map(t =>
          t.name === event.data.tool_name && t.status === 'started' ? { ...t, status: 'failed' } : t
        ))
        break
      case 'gui_element':
        setGuiElement(event.data)
        break
      case 'done': {
        const text = streamingRef.current
        if (text) {
          session.addMessage({ role: 'agent', content: text, timestamp: new Date().toISOString() })
        }
        streamingRef.current = ''
        setStreamingText('')
        setToolStatus([])
        break
      }
      case 'error':
        session.addMessage({ role: 'agent', content: event.data.message || 'Something went wrong', timestamp: new Date().toISOString() })
        streamingRef.current = ''
        setStreamingText('')
        setToolStatus([])
        break
    }
  }, [session])

  sse.onEvent(handleEvent)

  const handleSend = useCallback(async () => {
    const msg = inputValue.trim()
    if (!msg || sse.isStreaming) return
    setInputValue('')
    setStreamingText('')
    streamingRef.current = ''
    setToolStatus([])
    setGuiElement(null)
    const userMsg: AgentMessage = { role: 'user', content: msg, timestamp: new Date().toISOString() }
    session.addMessage(userMsg)
    await sse.sendMessage(msg, session.getContext(), session.mode)
  }, [inputValue, sse, session])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  if (!isOpen) return null
  const width = expanded ? 700 : 400

  return (
    <div
      className="fixed bottom-20 right-5 z-40 flex flex-col rounded-xl border border-border bg-background shadow-2xl transition-all duration-300"
      style={{ width, height: '70vh', maxHeight: 600 }}
      role="dialog"
      aria-label="AI Assistant"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">AI Assistant</span>
          <AgentModeSwitcher mode={session.mode} onModeChange={session.setMode} />
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setExpanded(!expanded)} aria-label={expanded ? 'Compact view' : 'Expanded view'} className="rounded p-1 hover:bg-muted">
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button onClick={onClose} aria-label="Close panel" className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <AgentMessageList messages={session.messages} streamingText={streamingText} toolStatus={toolStatus} welcomeMessage={session.messages.length === 0 && !streamingText} />
      {guiElement && (
        <div className="border-t border-border p-3">
          <AgentGuiRenderer definition={guiElement} onAction={(action, data) => {
            session.addMessage({ role: 'user', content: `[Action: ${action}] ${JSON.stringify(data)}`, timestamp: new Date().toISOString() })
          }} />
        </div>
      )}
      <div className="border-t border-border p-3">
        <div className="flex gap-2">
          <input ref={inputRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="Ask anything..." disabled={sse.isStreaming}
            className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Message input" />
          <button onClick={handleSend} disabled={!inputValue.trim() || sse.isStreaming} aria-label="Send message"
            className="rounded-lg bg-primary px-3 py-2 text-primary-foreground disabled:opacity-50">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
